/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import ShopDirectory from './pages/public/ShopDirectory';
import ShopProfile from './pages/public/ShopProfile';
import AdminDashboard from './pages/admin/AdminDashboard';
import ForProfessionals from './pages/public/ForProfessionals';
import Privacy from './pages/public/Privacy';
import Terms from './pages/public/Terms';
import Account from './pages/public/Account';
import MyBookings from './pages/public/MyBookings';
import RequireCustomer from './components/auth/RequireCustomer';

/**
 * The dashboard's route, shared with ScrollToTop below — it has to know which
 * path treats the hash as tab state rather than as a scroll anchor, and a bare
 * string in two places would drift the first time the route is renamed.
 */
const ADMIN_PATH = '/admin';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // When the URL has a hash (e.g. /#contact-us), scroll to that section
    // instead of the top. The target may not be in the DOM yet on the first
    // render, and its position can shift as page data loads — so poll briefly
    // and re-scroll to keep the section aligned.
    // The admin dashboard uses the hash for its own tab state, not for
    // scroll anchors — polling for an element by that name finds nothing.
    // Trailing slashes still match the route, so normalise before comparing.
    const isAdmin = pathname.replace(/\/+$/, '') === ADMIN_PATH;
    if (hash && !isAdmin) {
      const id = hash.slice(1);
      let attempts = 0;
      const interval = setInterval(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
        if (++attempts >= 8) clearInterval(interval);
      }, 150);
      return () => clearInterval(interval);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<ShopDirectory />} />
          <Route path="shop/:slug" element={<ShopProfile />} />
          <Route path="for-professionals" element={<ForProfessionals />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<Terms />} />
          <Route
            path="account"
            element={
              <RequireCustomer>
                <Account />
              </RequireCustomer>
            }
          />
          <Route
            path="account/bookings"
            element={
              <RequireCustomer>
                <MyBookings />
              </RequireCustomer>
            }
          />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<Terms />} />
        </Route>
        <Route path={ADMIN_PATH} element={<AdminDashboard />} />
      </Routes>
    </BrowserRouter>
  );
}
